/* =============================================================================
 * Mapped Pre-Order Product block
 * -----------------------------------------------------------------------------
 * Implements the runtime behavior for `snippets/mapped-preorder-product.liquid`.
 *
 * Responsibilities (per docs/mapped-preorder-mapping-spec.md and the plan):
 *   1. Idempotent init / destroy of each block instance, keyed by block.id.
 *   2. Build a (color, size) → variant lookup table for the mapped product.
 *   3. Listen to the main product's `variant:change` and variant-picker `change`
 *      events; resolve the mapped variant via the wildcard fallback chain (or
 *      `resolveMappedFromSelection` when a mapped-only color swatch is selected).
 *   4. Update CTA visibility when resolve finds a purchasable mapped variant
 *      (in stock or active mapped pre_order_date), plus enabled-state, label, price.
 *   5. On submit (real <form>) or button click (embed: wrapper is a <div>
 *      inside the product form to avoid nested forms), POST to /cart/add.js
 *      with optional line item properties and dispatch `ajaxProduct:added` so
 *      `theme.CartDrawer` refreshes + opens.
 *   6. Clean up on `shopify:section:unload` so theme-editor reloads do not leak
 *      listeners or stale state.
 *
 * Static asset (plain `.js`, NOT `.js.liquid`): every per-instance value is
 * passed in via data-* attributes on the block root.
 * ========================================================================== */

(function () {
  'use strict';

  if (window.__sscMappedPreorderLoaded) return;
  window.__sscMappedPreorderLoaded = true;

  var Core = window.ssc && window.ssc.MappedPreorderCore;
  if (!Core) {
    console.error(
      '[ssc-mapped-preorder]',
      'MappedPreorderCore missing — load mapped-preorder-core.js before mapped-preorder-product.js',
    );
    return;
  }

  var LOG_PREFIX = '[ssc-mapped-preorder]';
  var MAIN_PURCHASE_HIDDEN_CLASS = 'ssc-main-purchase-hidden';

  /** @type {Map<string, BlockState>} */
  var state = new Map();

  // Track the most recent main variant we saw per main product id, so a block
  // initialised AFTER its main product has already changed can still seed.
  /** @type {Map<number, object>} */
  var lastMainVariantByProductId = new Map();

  function parseJsonScript(root, selector) {
    return Core.parseJsonScript(root, selector);
  }

  function findMainProductContainer(root) {
    // Closest [data-section-type="product"] ancestor (the main product section
    // wraps the block via `{% for block in section.blocks %}`).
    var el = root;
    while (el && el !== document.body && el.nodeType === 1) {
      if (
        el.getAttribute &&
        el.getAttribute('data-section-type') === 'product'
      ) {
        return el;
      }
      el = el.parentNode;
    }
    return null;
  }

  function readMainVariantsFromContainer(productContainer) {
    if (!productContainer) return [];
    var ta = productContainer.querySelector('[data-variant-json]');
    if (!ta) return [];
    var raw = (ta.value !== undefined ? ta.value : ta.textContent) || '';
    raw = raw.trim();
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn(LOG_PREFIX, 'failed to parse main [data-variant-json]', e);
      return [];
    }
  }

  function readMainOptionsFromContainer(container) {
    if (!container) return [];
    var mappedRoot = container.querySelector('[data-ssc-mapped-preorder]');
    if (mappedRoot) {
      var fromMapped = parseJsonScript(mappedRoot, '[data-ssc-main-options]');
      if (Array.isArray(fromMapped) && fromMapped.length > 0) return fromMapped;
    }
    var wraps = container.querySelectorAll(
      '.variant-input-wrap[data-index^="option"]',
    );
    var options = [];
    for (var i = 0; i < wraps.length; i++) {
      var wrap = wraps[i];
      var name = wrap.getAttribute('name');
      if (!name) {
        var leg = wrap.querySelector('legend');
        name = leg ? (leg.textContent || '').trim() : '';
      }
      if (name) options.push({ name: name });
    }
    return options;
  }

  function findBlockStateForContainer(container) {
    if (!container) return null;
    var found = null;
    state.forEach(function (bs) {
      if (!found && bs.productContainer === container) found = bs;
    });
    return found;
  }

  function inferColorOptionName(container, mainOptions, blockState) {
    if (blockState && blockState.cfg && blockState.cfg.mainColorOption) {
      return blockState.cfg.mainColorOption;
    }
    var mappedRoot =
      container && container.querySelector('[data-ssc-mapped-preorder]');
    if (mappedRoot && mappedRoot.dataset.mainColorOption) {
      return mappedRoot.dataset.mainColorOption;
    }
    if (container) {
      var swatchInput = container.querySelector(
        '.variant__input--color-swatch[data-variant-input]',
      );
      if (swatchInput) {
        var wrap = swatchInput.closest('.variant-input-wrap');
        if (wrap && wrap.getAttribute('name')) return wrap.getAttribute('name');
      }
    }
    if (Array.isArray(mainOptions)) {
      for (var i = 0; i < mainOptions.length; i++) {
        var n = mainOptions[i] && mainOptions[i].name;
        if (n && /color|colour/i.test(n)) return n;
      }
    }
    return 'Color';
  }

  function inferSizeOptionName(container, mainOptions, blockState) {
    if (blockState && blockState.cfg && blockState.cfg.mainSizeOption) {
      return blockState.cfg.mainSizeOption;
    }
    var mappedRoot =
      container && container.querySelector('[data-ssc-mapped-preorder]');
    if (mappedRoot && mappedRoot.dataset.mainSizeOption) {
      return mappedRoot.dataset.mainSizeOption;
    }
    if (container) {
      var sizeWrap = container.querySelector(
        '.variant-input-wrap[data-handle="size"]',
      );
      if (sizeWrap && sizeWrap.getAttribute('name')) {
        return sizeWrap.getAttribute('name');
      }
    }
    if (Array.isArray(mainOptions)) {
      for (var j = 0; j < mainOptions.length; j++) {
        var name = mainOptions[j] && mainOptions[j].name;
        if (name && /^size$/i.test(name)) return name;
      }
    }
    return 'Size';
  }

  function buildMappedLookupFromRoot(mappedRoot) {
    if (!mappedRoot) return null;
    var mappedVariants = parseJsonScript(mappedRoot, '[data-ssc-mapped-variants]');
    var mappedOptions = parseJsonScript(mappedRoot, '[data-ssc-mapped-options]');
    if (!mappedVariants || !mappedOptions) return null;
    var cfg = readConfig(mappedRoot);
    return Core.buildLookup(
      mappedVariants,
      mappedOptions,
      cfg.mappedColorOption,
      cfg.mappedSizeOption,
    );
  }

  /**
   * Build context for Core.updateProductSizePickerAvailability (main + optional mapped).
   *
   * @param {Element} container
   * @param {BlockState|null} [blockState]
   * @returns {object|null}
   */
  function buildProductSizePickerCtx(container, blockState) {
    if (!container) return null;

    var bs = blockState || findBlockStateForContainer(container);
    var mainVariants =
      bs && Array.isArray(bs.mainVariants) && bs.mainVariants.length > 0
        ? bs.mainVariants
        : readMainVariantsFromContainer(container);
    if (!mainVariants.length) return null;

    var mainOptions =
      bs && Array.isArray(bs.mainOptions) && bs.mainOptions.length > 0
        ? bs.mainOptions
        : readMainOptionsFromContainer(container);
    if (!Array.isArray(mainOptions)) mainOptions = [];

    var mainDateMap =
      bs && bs.mainDateMap
        ? bs.mainDateMap
        : Core.parseDateMap(container, '[data-ssc-main-preorder-dates]');

    var ctx = {
      mainVariants: mainVariants,
      mainOptions: mainOptions,
      mainColorOptionName: inferColorOptionName(container, mainOptions, bs),
      mainSizeOptionName: inferSizeOptionName(container, mainOptions, bs),
      mainDateMap: mainDateMap,
      mappedLookup: null,
      mappedDateMap: Core.normalizeDateMap(null),
    };

    if (bs) {
      ctx.mappedLookup = bs.lookup;
      ctx.mappedDateMap = bs.dateMap;
    } else {
      var mappedRoot = container.querySelector('[data-ssc-mapped-preorder]');
      if (mappedRoot) {
        ctx.mappedLookup = buildMappedLookupFromRoot(mappedRoot);
        ctx.mappedDateMap = Core.parseDateMap(
          mappedRoot,
          '[data-ssc-mapped-preorder-dates]',
        );
      }
    }

    return ctx;
  }

  function hasButtonSizePicker(container) {
    if (!container || !container.querySelector) return false;
    if (container.querySelector('[data-ssc-size-input="true"]')) return true;
    var sizeWrap = container.querySelector(
      '.variant-input-wrap[data-handle="size"]',
    );
    return !!(
      sizeWrap && sizeWrap.querySelector('[data-variant-input]')
    );
  }

  /**
   * Slash/unslash size buttons for the current color (never touches color swatches).
   *
   * @param {Element|null} container
   * @param {string|null} [color] - selected color; inferred from DOM when omitted
   */
  function updateProductSizePickerForContainer(container, color) {
    if (!container || !hasButtonSizePicker(container)) return;
    var ctx = buildProductSizePickerCtx(container);
    if (!ctx) return;
    if (color != null && String(color).length > 0) ctx.color = color;
    Core.updateProductSizePickerAvailability(container, ctx);
  }

  function refreshProductSizePickersForVariant(variant) {
    var containers = document.querySelectorAll(
      '[data-section-type="product"]',
    );
    for (var i = 0; i < containers.length; i++) {
      var c = containers[i];
      if (!hasButtonSizePicker(c)) continue;
      if (variant && variant.product_id != null) {
        var mainVariants = readMainVariantsFromContainer(c);
        var first = mainVariants[0];
        if (
          first &&
          first.product_id != null &&
          Number(first.product_id) !== Number(variant.product_id)
        ) {
          continue;
        }
      }
      updateProductSizePickerForContainer(c);
    }
  }

  function bootstrapProductSizePickers() {
    var containers = document.querySelectorAll(
      '[data-section-type="product"]',
    );
    for (var i = 0; i < containers.length; i++) {
      updateProductSizePickerForContainer(containers[i]);
    }
  }

  function handleizeOptionName(name) {
    if (name == null) return '';
    return String(name)
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Read the current color/size from PDP variant pickers.
   *
   * @returns {{ mappedOnly: boolean, color: string|null, size: string|null }}
   */
  function readPickerSelection(container, cfg) {
    var result = { mappedOnly: false, color: null, size: null };
    if (!container) return result;

    var checkedInputs = container.querySelectorAll(
      '[data-variant-input]:checked',
    );

    for (var i = 0; i < checkedInputs.length; i++) {
      var inp = checkedInputs[i];
      var isColor =
        (inp.classList &&
          inp.classList.contains('variant__input--color-swatch')) ||
        inp.hasAttribute('data-color-name');
      if (!isColor) continue;

      var colorVal = inp.value || inp.getAttribute('data-color-name') || '';
      if (inp.getAttribute('data-ssc-mapped-only-color') === 'true') {
        result.mappedOnly = true;
        result.color = colorVal;
      } else if (!result.mappedOnly) {
        result.color = colorVal;
      }
    }

    var sizeName = (cfg && cfg.mainSizeOption) || '';
    var sizeHandle = handleizeOptionName(sizeName);
    var sizeInput = null;

    if (sizeHandle) {
      var wrap = container.querySelector(
        '.variant-input-wrap[data-handle="' + sizeHandle + '"]',
      );
      if (wrap) {
        sizeInput = wrap.querySelector('[data-variant-input]:checked');
      }
    }

    if (!sizeInput && sizeName) {
      for (var j = 0; j < checkedInputs.length; j++) {
        if (checkedInputs[j].name === sizeName) {
          sizeInput = checkedInputs[j];
          break;
        }
      }
    }

    if (!sizeInput) {
      for (var k = 0; k < checkedInputs.length; k++) {
        var candidate = checkedInputs[k];
        var isSizeCandidate =
          !(
            candidate.classList &&
            candidate.classList.contains('variant__input--color-swatch')
          ) && !candidate.hasAttribute('data-color-name');
        if (
          isSizeCandidate &&
          candidate.getAttribute('data-ssc-mapped-only-color') !== 'true'
        ) {
          sizeInput = candidate;
          break;
        }
      }
    }

    if (sizeInput) {
      result.size = sizeInput.value || null;
    }

    return result;
  }

  function readSelectedMainVariant(productContainer, mainVariants) {
    if (
      !productContainer ||
      !Array.isArray(mainVariants) ||
      mainVariants.length === 0
    )
      return null;
    var sel = productContainer.querySelector('[data-product-select]');
    var idStr = sel && sel.value;
    var id = idStr ? Number(idStr) : NaN;
    if (!isFinite(id)) {
      // Fall back to the first available variant, then first variant overall.
      for (var i = 0; i < mainVariants.length; i++) {
        if (mainVariants[i] && mainVariants[i].available)
          return mainVariants[i];
      }
      return mainVariants[0] || null;
    }
    for (var j = 0; j < mainVariants.length; j++) {
      if (mainVariants[j] && Number(mainVariants[j].id) === id)
        return mainVariants[j];
    }
    return null;
  }

  /**
   * Liquid `current_variant` from [data-current-variant-json], matched to mainVariants.
   *
   * @param {Element|null} productContainer
   * @param {Array} mainVariants
   * @returns {object|null}
   */
  function readCurrentVariantFromContainer(productContainer, mainVariants) {
    if (
      !productContainer ||
      !Array.isArray(mainVariants) ||
      mainVariants.length === 0
    )
      return null;
    var ta = productContainer.querySelector('[data-current-variant-json]');
    if (!ta) return null;
    var raw = (ta.value !== undefined ? ta.value : ta.textContent) || '';
    raw = raw.trim();
    if (!raw) return null;
    try {
      var current = JSON.parse(raw);
      if (!current || current.id == null) return null;
      var id = Number(current.id);
      if (!isFinite(id)) return null;
      for (var i = 0; i < mainVariants.length; i++) {
        if (mainVariants[i] && Number(mainVariants[i].id) === id)
          return mainVariants[i];
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  /**
   * Resolve the main variant used for mapped CTA / cart wildcard mapping.
   * Non-mapped-only: checked pickers → current_variant JSON → master select.
   *
   * @param {BlockState} blockState
   * @param {{ mappedOnly: boolean, color: string|null, size: string|null }|undefined} picker
   * @returns {object|null}
   */
  function resolveMainVariantForMapping(blockState, picker) {
    if (!blockState) return null;
    var productContainer = blockState.productContainer;
    var cfg = blockState.cfg;
    var mainVariants = blockState.mainVariants;
    var mainOptions = blockState.mainOptions;
    if (
      !Array.isArray(mainVariants) ||
      mainVariants.length === 0 ||
      !productContainer
    ) {
      return null;
    }

    if (!picker) {
      picker = readPickerSelection(productContainer, cfg);
    }

    if (!picker.mappedOnly && picker.color && picker.size) {
      var fromPickers = Core.findMainVariantByColorSize(
        mainVariants,
        mainOptions,
        cfg.mainColorOption,
        cfg.mainSizeOption,
        picker.color,
        picker.size,
      );
      if (fromPickers) return fromPickers;
    }

    var fromCurrentJson = readCurrentVariantFromContainer(
      productContainer,
      mainVariants,
    );
    if (fromCurrentJson) return fromCurrentJson;

    return readSelectedMainVariant(productContainer, mainVariants);
  }

  function ensureErrorEl(root) {
    var el = root.querySelector('[data-ssc-mapped-error]');
    if (el) return el;
    el = document.createElement('div');
    el.setAttribute('data-ssc-mapped-error', '');
    el.setAttribute('role', 'alert');
    el.className = 'ssc-mapped-preorder__error';
    el.style.display = 'none';
    var form = root.querySelector('[data-ssc-mapped-form]');
    if (form && form.parentNode) {
      form.parentNode.insertBefore(el, form.nextSibling);
    } else {
      root.appendChild(el);
    }
    return el;
  }

  function clearError(root) {
    var el = root.querySelector('[data-ssc-mapped-error]');
    if (el) {
      el.textContent = '';
      el.style.display = 'none';
    }
  }

  function showError(root, message) {
    var el = ensureErrorEl(root);
    el.textContent =
      message || 'Sorry, something went wrong adding this item to your cart.';
    el.style.display = '';
  }

  /* -------------------------------------------------------------------------
   * UI updates
   * --------------------------------------------------------------------- */

  function setMappedButtonVisible(blockState, visible) {
    var target = blockState.addWrap || blockState.btn;
    if (!target) return;
    if (visible) {
      target.classList.remove('tw:hidden');
    } else {
      target.classList.add('tw:hidden');
    }
  }

  function setButtonState(blockState, kind, label) {
    var btn = blockState.btn;
    var textEl = blockState.btnTextEl;
    if (!btn) return;
    if (textEl && typeof label === 'string') textEl.textContent = label;

    if (kind === 'available') {
      btn.disabled = false;
      btn.removeAttribute('disabled');
      btn.setAttribute('aria-disabled', 'false');
    } else {
      btn.disabled = true;
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-disabled', 'true');
    }
    btn.setAttribute('aria-busy', 'false');
  }

  function setHiddenVariantId(blockState, value) {
    if (blockState.idInput) {
      blockState.idInput.value = value == null ? '' : String(value);
    }
  }

  function renderPrice(blockState, variant) {
    var priceRoot = blockState.priceEl;
    var sepEl = blockState.priceSepEl;
    if (!priceRoot) return;
    if (!blockState.cfg.showPrice || !variant) {
      priceRoot.innerHTML = '';
      if (sepEl) sepEl.setAttribute('hidden', 'hidden');
      return;
    }
    if (sepEl) sepEl.removeAttribute('hidden');

    var html = '';
    if (
      blockState.cfg.showCompareAt &&
      variant.compare_at_price &&
      Number(variant.compare_at_price) > Number(variant.price)
    ) {
      html +=
        '<span class="ssc-mapped-preorder__price-compare tw:text-base tw:font-bold tw:line-through tw:opacity-70" data-ssc-mapped-price-compare>' +
        Core.formatMoney(variant.compare_at_price) +
        '</span> ';
    }
    html +=
      '<span class="ssc-mapped-preorder__price-current" data-ssc-mapped-price-current>' +
      Core.formatMoney(variant.price) +
      '</span>';
    priceRoot.innerHTML = html;
  }

  // Update the button's ship-date subline (`[data-ssc-mapped-ship-line]`) to
  // match the freshly-interpolated text. Caches the value on blockState so
  // buildProperties() can ship the exact same string with the cart line item.
  //
  // Skipped entirely when no `{estimated_ship_date_text}` template was set —
  // the server-rendered subheading fallback (handled in Liquid) stays put and
  // the cart property simply isn't sent.
  function updateShipLine(blockState, text) {
    blockState.currentShipText = text || '';
    if (
      !blockState.shipDateTemplate ||
      blockState.shipDateTemplate.length === 0
    )
      return;

    var el = blockState.shipLineEl;
    if (!el) return;

    if (text && text.length > 0) {
      el.textContent = text;
      el.classList.remove('tw:hidden');
    } else {
      el.textContent = '';
      el.classList.add('tw:hidden');
    }
  }

  /**
   * Mapped pre-order CTA is enabled only when the mapped variant is actually in
   * stock (`variant.available === true`). The `pre_order_date` metafield no
   * longer promotes a sold-out variant to "purchasable" – it only drives the
   * date label / CTA copy elsewhere. `dateMap` is retained for signature
   * stability across callers.
   *
   * @param {object|null} variant
   * @param {object|null} dateMap
   * @returns {boolean}
   */
  function isMappedVariantCtaEnabled(variant, dateMap) {
    if (!variant || !variant.id) return false;
    return variant.available === true;
  }

  /**
   * Hide main quantity / ATC / buy-now when only mapped pre-order stock is purchasable.
   *
   * @param {BlockState} blockState
   * @param {object|null} mainVariant
   * @param {object|null} mappedVariant
   * @returns {boolean}
   */
  function shouldHideMainPurchase(blockState, mainVariant, mappedVariant) {
    if (!blockState.cfg.hideMainPurchaseWhenPreorderOnly) return false;
    if (!mainVariant || !mainVariant.id) return false;
    if (mainVariant.available === true) return false;
    if (Core.hasPreorderForVariant(blockState.mainDateMap, mainVariant)) {
      return false;
    }
    return isMappedVariantCtaEnabled(mappedVariant, blockState.dateMap);
  }

  /**
   * Strict color+size mapped lookup for hide rules (no wildcard fallbacks).
   *
   * @param {BlockState} blockState
   * @param {{ color: string|null, size: string|null }|null} picker
   * @param {object|null} mainVariant
   * @returns {object|null}
   */
  function resolveStrictMappedForHide(blockState, picker, mainVariant) {
    if (!blockState || !blockState.lookup) return null;

    if (picker && (picker.color != null || picker.size != null)) {
      var fromPicker = Core.lookupMappedByColorSizeForAvailability(
        picker.color,
        picker.size,
        blockState.lookup,
      );
      if (fromPicker) return fromPicker;
    }

    if (!mainVariant || !mainVariant.id) return null;

    var mainOptions = blockState.mainOptions;
    var cfg = blockState.cfg;
    var mainColorIdx = Core.indexOfOption(mainOptions, cfg.mainColorOption);
    var mainSizeIdx = Core.indexOfOption(mainOptions, cfg.mainSizeOption);
    var opts = Array.isArray(mainVariant.options) ? mainVariant.options : [];
    var color = mainColorIdx >= 0 ? opts[mainColorIdx] : '*';
    var size = mainSizeIdx >= 0 ? opts[mainSizeIdx] : '*';

    return Core.lookupMappedByColorSizeForAvailability(
      color,
      size,
      blockState.lookup,
    );
  }

  /**
   * Hide mapped pre-order CTA when main is in stock and mapped CTA would show.
   *
   * @param {BlockState} blockState
   * @param {object|null} mainVariant
   * @param {object|null} mappedVariant - strict color+size match
   * @returns {boolean}
   */
  function shouldHideMappedPreorderWhenMainInStock(
    blockState,
    mainVariant,
    mappedVariant,
  ) {
    if (!blockState.cfg.hidePreorderWhenMainInStock) return false;
    if (!mainVariant || !mainVariant.id) return false;
    if (mainVariant.available !== true) return false;
    if (!mappedVariant || !mappedVariant.id) return false;
    return isMappedVariantCtaEnabled(mappedVariant, blockState.dateMap);
  }

  function setMainPurchaseUiHiddenOnElements(elements, hidden) {
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el || !el.classList) continue;
      if (hidden) {
        el.classList.add(MAIN_PURCHASE_HIDDEN_CLASS);
        el.setAttribute('hidden', 'hidden');
      } else {
        el.classList.remove(MAIN_PURCHASE_HIDDEN_CLASS);
        el.removeAttribute('hidden');
      }
    }
  }

  /**
   * Toggle `[data-ssc-main-purchase-ui]` (quantity, ATC, dynamic checkout).
   * Falls back to legacy selectors when the wrapper is absent.
   *
   * @param {BlockState} blockState
   * @param {boolean} hidden
   */
  function setMainPurchaseUiHidden(blockState, hidden) {
    var container = blockState.productContainer;
    if (!container) return;

    var wrappers = container.querySelectorAll('[data-ssc-main-purchase-ui]');
    if (wrappers.length) {
      setMainPurchaseUiHiddenOnElements(wrappers, hidden);
    } else {
      var fallbackTargets = [];
      var qtyEls = container.querySelectorAll('.product__quantity');
      for (var q = 0; q < qtyEls.length; q++) fallbackTargets.push(qtyEls[q]);

      var paymentEls = container.querySelectorAll('.payment-buttons');
      for (var p = 0; p < paymentEls.length; p++) fallbackTargets.push(paymentEls[p]);

      var atcEls = container.querySelectorAll(
        '[data-add-to-cart], button[id^="AddToCart-"], input[id^="AddToCart-"]',
      );
      for (var a = 0; a < atcEls.length; a++) {
        var atc = atcEls[a];
        var atcParent = atc.closest('.grid__item') || atc.parentElement;
        fallbackTargets.push(atcParent || atc);
      }

      setMainPurchaseUiHiddenOnElements(fallbackTargets, hidden);
    }

    blockState.mainPurchaseUiHidden = hidden;
  }

  function applyState(blockState, variant, strictMappedForHide) {
    blockState.matchedVariant = variant && variant.id ? variant : null;

    // Resolve and interpolate the ship-date text on every state transition so
    // the visible subline and the cart property stay in lock-step with the
    // currently matched mapped variant. Done before the early returns so the
    // text is correct even when the button is hidden.
    var resolvedDate = Core.resolveShipDate(
      blockState.dateMap,
      blockState.matchedVariant,
    );
    var shipText = Core.interpolateShipText(
      blockState.shipDateTemplate,
      resolvedDate,
    );
    updateShipLine(blockState, shipText);

    if (!variant || !variant.id) {
      setHiddenVariantId(blockState, '');
      renderPrice(blockState, null);
      setButtonState(blockState, 'disabled', blockState.cfg.unavailableText);
      setMappedButtonVisible(blockState, false);
      clearError(blockState.root);
      return;
    }

    if (!isMappedVariantCtaEnabled(variant, blockState.dateMap)) {
      setHiddenVariantId(blockState, '');
      renderPrice(blockState, variant);
      setButtonState(blockState, 'disabled', blockState.cfg.soldOutText);
      setMappedButtonVisible(blockState, false);
      clearError(blockState.root);
      return;
    }

    setHiddenVariantId(blockState, variant.id);
    renderPrice(blockState, variant);
    setButtonState(blockState, 'available', blockState.cfg.buttonText);

    var showMappedButton = true;
    if (!blockState.mappedOnly) {
      var strictForHide =
        strictMappedForHide !== undefined
          ? strictMappedForHide
          : resolveStrictMappedForHide(
              blockState,
              null,
              blockState.mainVariant,
            );
      if (
        shouldHideMappedPreorderWhenMainInStock(
          blockState,
          blockState.mainVariant,
          strictForHide,
        )
      ) {
        showMappedButton = false;
      }
    }
    setMappedButtonVisible(blockState, showMappedButton);
    clearError(blockState.root);
  }

  function updateBodyMappedOnlyClass() {
    var anySuppressed = false;
    state.forEach(function (bs) {
      if (bs.mainAtcSuppressed) anySuppressed = true;
    });
    if (anySuppressed) {
      document.body.classList.add('ssc-mapped-only-picker-active');
    } else {
      document.body.classList.remove('ssc-mapped-only-picker-active');
    }
  }

  function setMainAtcSuppressed(blockState, suppressed) {
    var container = blockState.productContainer;
    if (!container) return;

    var buttons = container.querySelectorAll(
      '[data-add-to-cart], button[id^="AddToCart-"], input[id^="AddToCart-"]',
    );

    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (suppressed) {
        if (!btn.hasAttribute('data-ssc-main-atc-was-disabled')) {
          btn.setAttribute(
            'data-ssc-main-atc-was-disabled',
            btn.disabled ? '1' : '0',
          );
        }
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
      } else if (blockState.mainAtcSuppressed) {
        var was = btn.getAttribute('data-ssc-main-atc-was-disabled');
        btn.removeAttribute('data-ssc-main-atc-was-disabled');
        if (was === '0') {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
        }
      }
    }

    blockState.mainAtcSuppressed = suppressed;

    if (suppressed) {
      container.classList.add('ssc-mapped-only-picker-active');
    } else {
      container.classList.remove('ssc-mapped-only-picker-active');
    }
    updateBodyMappedOnlyClass();
  }

  function syncMainAtcForMappedOnly(blockState, mappedVariant) {
    if (blockState.cfg.hideMainPurchaseWhenPreorderOnly) {
      setMainAtcSuppressed(blockState, false);
      return;
    }
    var suppress =
      blockState.mappedOnly === true &&
      mappedVariant &&
      mappedVariant.id &&
      mappedVariant.available === true;
    setMainAtcSuppressed(blockState, suppress);
  }

  function getGalleryMediaKey(mappedVariant, colorName) {
    if (mappedVariant) {
      if (mappedVariant.featured_media && mappedVariant.featured_media.id != null) {
        return 'media:' + mappedVariant.featured_media.id;
      }
      var src = Core.getVariantFeaturedSrc(mappedVariant);
      if (src) return 'src:' + src;
    }
    if (colorName) {
      return 'color:' + Core.slugifyImageSetName(colorName);
    }
    return '';
  }

  function shouldSyncGallery(container, mappedVariant, colorName) {
    if (mappedVariant && Core.getVariantFeaturedSrc(mappedVariant)) return true;
    if (!colorName || !container) return false;
    var mainSlider = container.querySelector('[data-product-photos]');
    return !!(mainSlider && mainSlider.querySelector('[data-set-name]'));
  }

  function findColorOptionWrap(container, mainColorOption) {
    if (!container) return null;
    var colorHandle = handleizeOptionName(mainColorOption);
    var wrap = null;
    if (colorHandle) {
      wrap = container.querySelector(
        '.variant-input-wrap[data-handle="' + colorHandle + '"]',
      );
    }
    if (!wrap) {
      var mainSlider = container.querySelector('[data-product-photos]');
      var setNameEl = mainSlider && mainSlider.querySelector('[data-set-name]');
      var setName = setNameEl && setNameEl.getAttribute('data-set-name');
      if (setName) {
        wrap = container.querySelector(
          '.variant-input-wrap[data-handle="' + setName + '"]',
        );
      }
    }
    return wrap;
  }

  function trySyncImageSetGallery(container, colorName, mainColorOption) {
    if (!container || !colorName) return false;

    var mainSlider = container.querySelector('[data-product-photos]');
    if (!mainSlider || !mainSlider.querySelector('[data-set-name]')) return false;

    var wrap = findColorOptionWrap(container, mainColorOption);
    if (!wrap) return false;

    var imageSetIndex = wrap.getAttribute('data-index');
    if (!imageSetIndex) return false;

    var syntheticVariant = Object.create(null);
    syntheticVariant[imageSetIndex] = colorName;

    container.dispatchEvent(
      new CustomEvent('variantChange', {
        detail: { variant: syntheticVariant },
      }),
    );
    return true;
  }

  function trySyncThumbGallery(container, mappedVariant) {
    if (!container || !mappedVariant) return false;

    var media = mappedVariant.featured_media;
    if (!media || media.id == null) return false;

    var thumb = container.querySelector(
      '.product__thumb[data-id="' + media.id + '"]',
    );
    if (!thumb) return false;

    var imageIndex = thumb.getAttribute('data-index');
    if (imageIndex == null || imageIndex === '') return false;

    var previewSrc =
      (media.preview_image && media.preview_image.src) ||
      Core.getVariantFeaturedSrc(mappedVariant);
    if (!previewSrc) return false;

    var variantForEvent = {
      featured_media: {
        id: media.id,
        preview_image: { src: previewSrc },
      },
    };
    if (mappedVariant.featured_image) {
      variantForEvent.featured_image = mappedVariant.featured_image;
    }

    container.dispatchEvent(
      new CustomEvent('variantImageChange', {
        detail: { variant: variantForEvent },
      }),
    );
    return true;
  }

  function getMainGalleryImageSize(container, blockState) {
    if (blockState.galleryImageSize) return blockState.galleryImageSize;

    var imageSize = '620x';
    try {
      if (
        window.theme &&
        window.theme.Images &&
        typeof window.theme.Images.imageSize === 'function'
      ) {
        var mainSlider = container.querySelector('[data-product-photos]');
        var firstImg = mainSlider && mainSlider.querySelector('img');
        if (firstImg) {
          var fromSrc = firstImg.currentSrc || firstImg.src;
          var parsed = window.theme.Images.imageSize(fromSrc);
          if (parsed) imageSize = parsed;
        }
      }
    } catch (e) {
      /* noop */
    }

    blockState.galleryImageSize = imageSize;
    return imageSize;
  }

  function trySyncDirectImageFallback(container, mappedVariant, blockState) {
    var rawSrc = Core.getVariantFeaturedSrc(mappedVariant);
    if (!rawSrc || !container) return false;

    var mainSlider = container.querySelector('[data-product-photos]');
    if (!mainSlider) return false;

    var slide =
      mainSlider.querySelector('.product-main-slide.is-selected') ||
      mainSlider.querySelector('.flickity-slider .product-main-slide') ||
      mainSlider.querySelector('.product-main-slide');
    if (!slide) return false;

    var img =
      slide.querySelector('[data-product-image-main] img') ||
      slide.querySelector('img');
    if (!img) return false;

    var imageSize = getMainGalleryImageSize(container, blockState);
    var sizedUrl = rawSrc;
    try {
      if (
        window.theme &&
        window.theme.Images &&
        typeof window.theme.Images.getSizedImageUrl === 'function'
      ) {
        sizedUrl =
          window.theme.Images.getSizedImageUrl(rawSrc, imageSize) || rawSrc;
      }
    } catch (e) {
      /* use rawSrc */
    }

    img.src = sizedUrl;
    if (img.hasAttribute('data-src')) {
      img.setAttribute('data-src', sizedUrl);
    }
    if (img.hasAttribute('data-srcset')) {
      img.setAttribute('data-srcset', sizedUrl);
    }
    if (img.classList) {
      img.classList.add('lazyloaded');
      img.classList.remove('lazyload');
    }

    try {
      var flkty =
        mainSlider.flickity ||
        (window.Flickity && typeof window.Flickity.data === 'function'
          ? window.Flickity.data(mainSlider)
          : null);
      if (flkty && typeof flkty.resize === 'function') {
        flkty.resize();
      }
    } catch (e) {
      /* noop */
    }

    return true;
  }

  /**
   * Sync main PDP gallery when a mapped-only color is selected (theme.Variants
   * does not fire variantImageChange because there is no matching main variant).
   *
   * @param {Element|null} container - [data-section-type="product"] root
   * @param {object|null} mappedVariant - resolved mapped variant, if any
   * @param {string|null} colorName - selected mapped-only color label
   * @param {BlockState} blockState
   */
  function syncProductGallery(container, mappedVariant, colorName, blockState) {
    if (!container || !blockState) return;
    if (!shouldSyncGallery(container, mappedVariant, colorName)) return;

    var mediaKey = getGalleryMediaKey(mappedVariant, colorName);
    if (!mediaKey) return;
    if (blockState.lastGalleryMediaKey === mediaKey) return;

    var synced = false;
    if (trySyncImageSetGallery(container, colorName, blockState.cfg.mainColorOption)) {
      synced = true;
    } else if (trySyncThumbGallery(container, mappedVariant)) {
      synced = true;
    } else if (trySyncDirectImageFallback(container, mappedVariant, blockState)) {
      synced = true;
    }

    if (synced) {
      blockState.lastGalleryMediaKey = mediaKey;
    }
  }

  function recompute(blockState) {
    var picker = readPickerSelection(
      blockState.productContainer,
      blockState.cfg,
    );

    if (picker.mappedOnly) {
      blockState.mappedOnly = true;
      blockState.mainVariant = null;
      var mappedOnlyResolved = Core.resolveMappedFromSelection(
        picker.color,
        picker.size,
        blockState.lookup,
      );
      applyState(blockState, mappedOnlyResolved);
      if (blockState.cfg.hideMainPurchaseWhenPreorderOnly) {
        setMainPurchaseUiHidden(
          blockState,
          isMappedVariantCtaEnabled(mappedOnlyResolved, blockState.dateMap),
        );
      } else {
        setMainPurchaseUiHidden(blockState, false);
      }
      syncMainAtcForMappedOnly(blockState, mappedOnlyResolved);
      syncProductGallery(
        blockState.productContainer,
        mappedOnlyResolved,
        picker.color,
        blockState,
      );
      updateProductSizePickerForContainer(
        blockState.productContainer,
        picker.color,
      );
      return;
    }

    blockState.mappedOnly = false;
    blockState.lastGalleryMediaKey = '';

    var mainVariant = resolveMainVariantForMapping(blockState, picker);
    blockState.mainVariant = mainVariant;

    var resolved = Core.resolveMapped(
      blockState.mainVariant,
      blockState.mainOptions,
      blockState.cfg.mainColorOption,
      blockState.cfg.mainSizeOption,
      blockState.lookup,
    );
    var strictMappedForHide = resolveStrictMappedForHide(
      blockState,
      picker,
      mainVariant,
    );
    applyState(blockState, resolved, strictMappedForHide);
    if (blockState.cfg.hideMainPurchaseWhenPreorderOnly) {
      setMainPurchaseUiHidden(
        blockState,
        shouldHideMainPurchase(blockState, mainVariant, resolved),
      );
    } else {
      setMainPurchaseUiHidden(blockState, false);
    }
    syncMainAtcForMappedOnly(blockState, null);
    updateProductSizePickerForContainer(
      blockState.productContainer,
      picker.color,
    );
  }

  /* -------------------------------------------------------------------------
   * Submit handler
   * --------------------------------------------------------------------- */

  function buildProperties(blockState) {
    var cfg = blockState.cfg;
    return Core.buildProperties({
      enableProperties: cfg.enableProperties,
      purchaseTypeLabel: cfg.purchaseTypeLabel,
      shipDateTemplate: blockState.shipDateTemplate,
      shipText: blockState.currentShipText,
      dateMap: blockState.dateMap,
      matchedVariant: blockState.matchedVariant,
      lookup: blockState.lookup,
      mainVariant: blockState.mainVariant,
      parentHandle: blockState.parentHandle,
      lipKeys: {
        purchaseType: cfg.lipPurchaseTypeKey,
        shipDate: cfg.lipShipDateKey,
        parentHandle: cfg.lipParentHandleKey,
        parentVariantId: cfg.lipParentVariantIdKey,
        color: cfg.lipColorKey,
        size: cfg.lipSizeKey,
      },
    });
  }

  function setBusy(blockState, isBusy) {
    var btn = blockState.btn;
    var textEl = blockState.btnTextEl;
    if (!btn) return;
    if (isBusy) {
      blockState.savedBtnLabel = textEl ? textEl.textContent : '';
      btn.disabled = true;
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-busy', 'true');
      if (textEl) textEl.textContent = 'Adding…';
    } else {
      btn.setAttribute('aria-busy', 'false');
      if (textEl && typeof blockState.savedBtnLabel === 'string') {
        textEl.textContent = blockState.savedBtnLabel;
      }
      // Re-evaluate enabled state from current matched variant.
      var matched = blockState.matchedVariant;
      if (matched && isMappedVariantCtaEnabled(matched, blockState.dateMap)) {
        btn.disabled = false;
        btn.removeAttribute('disabled');
        btn.setAttribute('aria-disabled', 'false');
        setMappedButtonVisible(blockState, true);
      } else {
        btn.disabled = true;
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        setMappedButtonVisible(blockState, false);
      }
    }
  }

  function onSubmit(blockState, evt) {
    if (evt && evt.preventDefault) evt.preventDefault();

    var matched = blockState.matchedVariant;
    if (!isMappedVariantCtaEnabled(matched, blockState.dateMap)) {
      // Defensive: button should already be disabled in these states.
      return;
    }

    clearError(blockState.root);

    var qtyInput = blockState.root.querySelector('[data-ssc-mapped-quantity]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (!isFinite(qty) || qty < 1) qty = 1;

    var payload = {
      id: Number(matched.id),
      quantity: qty,
    };
    var properties = buildProperties(blockState);
    if (properties) payload.properties = properties;

    setBusy(blockState, true);

    Core.cartAdd(payload, {
      addToCartBtn: blockState.btn,
      onSuccess: function () {
        setBusy(blockState, false);
      },
      onError: function (msg) {
        setBusy(blockState, false);
        showError(blockState.root, msg);
      },
    }).catch(function () {
      setBusy(blockState, false);
    });
  }

  /* -------------------------------------------------------------------------
   * Init / destroy
   * --------------------------------------------------------------------- */

  function readConfig(root) {
    var ds = root.dataset;
    var cycleSec = parseFloat(ds.borderGradientCycleSec);
    if (!isFinite(cycleSec) || cycleSec <= 0) cycleSec = 8;
    return {
      blockId: ds.blockId || '',
      sectionId: ds.sectionId || '',
      mainColorOption: ds.mainColorOption || '',
      mainSizeOption: ds.mainSizeOption || '',
      mappedColorOption: ds.mappedColorOption || '',
      mappedSizeOption: ds.mappedSizeOption || '',
      purchaseTypeLabel: ds.purchaseTypeLabel || '',
      shipDateText: ds.shipDateText || '',
      enableProperties: (ds.enableProperties || '').toLowerCase() === 'true',
      lipPurchaseTypeKey: ds.lipPurchaseTypeKey || '',
      lipShipDateKey: ds.lipShipDateKey || '',
      lipParentHandleKey: ds.lipParentHandleKey || '',
      lipParentVariantIdKey: ds.lipParentVariantIdKey || '',
      lipColorKey: ds.lipColorKey || '',
      lipSizeKey: ds.lipSizeKey || '',
      buttonText: ds.buttonText || '',
      unavailableText: ds.unavailableText || '',
      soldOutText: ds.soldOutText || '',
      showPrice: (ds.showPrice || 'true').toLowerCase() === 'true',
      showCompareAt: (ds.showCompareAt || 'true').toLowerCase() === 'true',
      borderGradientEnabled:
        (ds.borderGradientEnabled || '').toLowerCase() === 'true',
      borderGradientCycleSec: cycleSec,
      hideMainPurchaseWhenPreorderOnly:
        (ds.hideMainPurchaseWhenPreorderOnly || '').toLowerCase() === 'true',
      hidePreorderWhenMainInStock:
        (ds.hidePreorderWhenMainInStock || '').toLowerCase() === 'true',
    };
  }

  function prefersReducedMotion() {
    try {
      return (
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    } catch (e) {
      return false;
    }
  }

  function stopBorderGradient(blockState) {
    blockState.borderGradientActive = false;
    if (blockState.borderGradientRaf != null) {
      cancelAnimationFrame(blockState.borderGradientRaf);
      blockState.borderGradientRaf = null;
    }
  }

  function startBorderGradient(blockState) {
    if (!blockState.btn || !blockState.cfg.borderGradientEnabled) return;
    if (prefersReducedMotion()) return;

    stopBorderGradient(blockState);

    var btn = blockState.btn;
    var durationMs = Math.max(
      500,
      (Number(blockState.cfg.borderGradientCycleSec) || 8) * 1000,
    );
    var t0 = performance.now();

    function frame(now) {
      if (!blockState.borderGradientActive) return;
      var angle = (((now - t0) % durationMs) / durationMs) * 360;
      btn.style.setProperty('--gradient-angle', angle + 'deg');
      blockState.borderGradientRaf = requestAnimationFrame(frame);
    }

    blockState.borderGradientActive = true;
    blockState.borderGradientRaf = requestAnimationFrame(frame);
  }

  function init(root) {
    if (!root || root.dataset.sscInited === '1') return;

    var cfg = readConfig(root);
    if (!cfg.blockId) {
      console.warn(LOG_PREFIX, 'block missing data-block-id; skipping init');
      return;
    }

    // If a previous state entry exists for this block id (e.g. theme editor
    // re-rendered without firing unload first), tear it down before re-initing.
    if (state.has(cfg.blockId)) {
      destroy(state.get(cfg.blockId).root);
    }

    var mappedVariants = parseJsonScript(root, '[data-ssc-mapped-variants]');
    var mappedOptions = parseJsonScript(root, '[data-ssc-mapped-options]');
    var mainOptions = parseJsonScript(root, '[data-ssc-main-options]');

    if (!mappedVariants || !mappedOptions || !mainOptions) {
      console.error(
        LOG_PREFIX,
        'init aborted: failed to parse one or more JSON payloads',
        cfg.blockId,
      );
      // Leave block in its server-rendered disabled state.
      root.dataset.sscInited = '1';
      return;
    }

    // Pre-order ship dates: parsed once at init, looked up by matched variant
    // id on every recompute. Always returns a normalized shape so resolution
    // doesn't need null-guards.
    var dateMap = Core.parseDateMap(root, '[data-ssc-mapped-preorder-dates]');

    var lookup = Core.buildLookup(
      mappedVariants,
      mappedOptions,
      cfg.mappedColorOption,
      cfg.mappedSizeOption,
    );

    var productContainer = findMainProductContainer(root);
    var mainVariants = readMainVariantsFromContainer(productContainer);
    var mainDateMap = Core.parseDateMap(
      productContainer,
      '[data-ssc-main-preorder-dates]',
    );
    var mainVariantsById = Object.create(null);
    for (var i = 0; i < mainVariants.length; i++) {
      if (mainVariants[i] && mainVariants[i].id != null) {
        mainVariantsById[String(mainVariants[i].id)] = mainVariants[i];
      }
    }

    var form = root.querySelector('[data-ssc-mapped-form]');
    var addWrap = root.querySelector('[data-ssc-mapped-add-wrap]');
    var btn = root.querySelector('[data-ssc-mapped-add]');
    var btnTextEl = root.querySelector('[data-ssc-mapped-add-text]');
    var idInput = root.querySelector('[data-ssc-mapped-variant-id]');
    var priceEl = root.querySelector('[data-ssc-mapped-price]');
    var priceSepEl = root.querySelector('[data-ssc-mapped-price-sep]');
    var shipLineEl = root.querySelector('[data-ssc-mapped-ship-line]');

    /** @type {BlockState} */
    var blockState = {
      cfg: cfg,
      root: root,
      productContainer: productContainer,
      mappedVariants: mappedVariants,
      mappedOptions: mappedOptions,
      mainOptions: mainOptions,
      mainVariants: mainVariants,
      mainDateMap: mainDateMap,
      mainVariantsById: mainVariantsById,
      lookup: lookup,
      dateMap: dateMap,
      shipDateTemplate: cfg.shipDateText,
      currentShipText: '',
      mainVariant: null,
      mappedOnly: false,
      mainAtcSuppressed: false,
      mainPurchaseUiHidden: false,
      matchedVariant: null,
      parentHandle: root.dataset.parentProductHandle || '',
      form: form,
      addWrap: addWrap,
      btn: btn,
      btnTextEl: btnTextEl,
      idInput: idInput,
      priceEl: priceEl,
      priceSepEl: priceSepEl,
      shipLineEl: shipLineEl,
      submitHandler: null,
      savedBtnLabel: btnTextEl ? btnTextEl.textContent : '',
      borderGradientRaf: null,
      borderGradientActive: false,
      lastGalleryMediaKey: '',
      galleryImageSize: null,
    };

    blockState.submitHandler = function (evt) {
      onSubmit(blockState, evt);
    };
    // Standalone block: <form data-ssc-mapped-form>. Embedded in product form:
    // wrapper is a <div data-ssc-mapped-form> + type="button" — only click fires.
    if (form && String(form.tagName || '').toUpperCase() === 'FORM') {
      form.addEventListener('submit', blockState.submitHandler);
    } else if (btn) {
      btn.addEventListener('click', blockState.submitHandler);
    }

    // Seed the initial main variant: prefer the most recent variant we observed
    // for this product (in case `variant:change` fired before init), otherwise
    // align with checked pickers (not only the master <select>).
    var seeded = null;
    var initPicker = readPickerSelection(productContainer, cfg);
    if (!initPicker.mappedOnly) {
      var firstMain = mainVariants[0];
      if (
        firstMain &&
        firstMain.product_id != null &&
        lastMainVariantByProductId.has(firstMain.product_id)
      ) {
        seeded = lastMainVariantByProductId.get(firstMain.product_id);
      }
      if (!seeded) {
        seeded = resolveMainVariantForMapping(blockState, initPicker);
      }
    }
    blockState.mainVariant = seeded;

    state.set(cfg.blockId, blockState);
    root.dataset.sscInited = '1';

    recompute(blockState);
    startBorderGradient(blockState);
  }

  function destroy(root) {
    if (!root) return;
    var blockId = root.dataset.blockId;
    if (!blockId) return;
    var blockState = state.get(blockId);
    if (blockState) {
      stopBorderGradient(blockState);
      if (blockState.mainPurchaseUiHidden) {
        setMainPurchaseUiHidden(blockState, false);
      }
      if (blockState.mainAtcSuppressed) {
        setMainAtcSuppressed(blockState, false);
      }
      if (blockState.submitHandler) {
        if (
          blockState.form &&
          String(blockState.form.tagName || '').toUpperCase() === 'FORM'
        ) {
          blockState.form.removeEventListener(
            'submit',
            blockState.submitHandler,
          );
        } else if (blockState.btn) {
          blockState.btn.removeEventListener('click', blockState.submitHandler);
        }
      }
      state.delete(blockId);
    }
    delete root.dataset.sscInited;
  }

  /* -------------------------------------------------------------------------
   * Document-level listeners (registered ONCE at module load)
   * --------------------------------------------------------------------- */

  function onVariantChange(detail) {
    var variant = detail && detail.variant ? detail.variant : null;

    if (variant && variant.id != null && variant.product_id != null) {
      lastMainVariantByProductId.set(variant.product_id, variant);
    }

    state.forEach(function (blockState) {
      var matchesThisBlock = false;

      if (variant && variant.id != null) {
        // The fast path: the dispatched variant id is one of THIS block's main
        // product variants — definitely our event.
        if (blockState.mainVariantsById[String(variant.id)]) {
          matchesThisBlock = true;
        } else if (
          variant.product_id != null &&
          blockState.mainVariantsById[
            Object.keys(blockState.mainVariantsById)[0]
          ] &&
          Number(
            blockState.mainVariantsById[
              Object.keys(blockState.mainVariantsById)[0]
            ].product_id,
          ) === Number(variant.product_id)
        ) {
          // Same product_id but unknown variant id (e.g. a newly-added variant
          // not in the cached JSON dump). Still ours.
          matchesThisBlock = true;
        }
      } else {
        // detail.variant === null → "no variant matches selection". Force
        // unavailable on every block within the same section as the dispatcher.
        matchesThisBlock = true;
      }

      if (!matchesThisBlock) return;

      if (variant && variant.id != null) {
        blockState.mainVariant = variant;
        blockState.mappedOnly = false;
      } else {
        blockState.mainVariant = null;
      }
      recompute(blockState);
    });
  }

  function onPickerChange(container) {
    if (!container) return;
    state.forEach(function (blockState) {
      if (blockState.productContainer === container) {
        recompute(blockState);
      }
    });
    updateProductSizePickerForContainer(container);
  }

  function bootstrap() {
    var roots = document.querySelectorAll('[data-ssc-mapped-preorder]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
    bootstrapProductSizePickers();
  }

  document.addEventListener('variant:change', function (e) {
    var detail = e && e.detail;
    onVariantChange(detail);
    refreshProductSizePickersForVariant(detail && detail.variant);
  });

  document.addEventListener('change', function (e) {
    var target = e.target;
    if (
      !target ||
      !target.matches ||
      !target.matches('[data-variant-input]')
    ) {
      return;
    }
    var container = findMainProductContainer(target);
    if (!container) return;
    onPickerChange(container);
  });

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    var roots = e.target.querySelectorAll('[data-ssc-mapped-preorder]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
    var productSections = e.target.querySelectorAll(
      '[data-section-type="product"]',
    );
    for (var j = 0; j < productSections.length; j++) {
      updateProductSizePickerForContainer(productSections[j]);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    var roots = e.target.querySelectorAll('[data-ssc-mapped-preorder]');
    for (var i = 0; i < roots.length; i++) destroy(roots[i]);
  });

  document.addEventListener('shopify:block:select', function (e) {
    if (!e.target || !e.target.querySelector) return;
    var root =
      e.target.matches && e.target.matches('[data-ssc-mapped-preorder]')
        ? e.target
        : e.target.querySelector('[data-ssc-mapped-preorder]');
    if (root && typeof root.scrollIntoView === 'function') {
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Public surface for testing / debugging.
  window.ssc = window.ssc || {};
  window.ssc.MappedPreorder = {
    init: init,
    destroy: destroy,
    state: state,
    readPickerSelection: readPickerSelection,
    resolveMainVariantForMapping: resolveMainVariantForMapping,
    isMappedVariantCtaEnabled: isMappedVariantCtaEnabled,
    shouldHideMainPurchase: shouldHideMainPurchase,
    shouldHideMappedPreorderWhenMainInStock:
      shouldHideMappedPreorderWhenMainInStock,
    setMainPurchaseUiHidden: setMainPurchaseUiHidden,
    recompute: recompute,
    syncProductGallery: syncProductGallery,
    buildProductSizePickerCtx: buildProductSizePickerCtx,
    updateProductSizePickerForContainer: updateProductSizePickerForContainer,
    bootstrapProductSizePickers: bootstrapProductSizePickers,
    _internals: Core,
  };
})();
